import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect, cn } from '@ibe/ui';
import { useEffect, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useWatch,
} from 'react-hook-form';

import { dialCodeOptions } from '../../../lib/forms/dial-codes.js';
import { TIMEZONE_OPTIONS } from '../../../lib/forms/timezones.js';
import { useCities, useCountries, useModuleCatalogue, useStates } from '../api/queries.js';
import { type TenantFormInput } from '../api/schemas.js';

export interface TenantFieldsProps {
  readonly register: UseFormRegister<TenantFormInput>;
  readonly errors: FieldErrors<TenantFormInput>;
  /**
   * Needed by `Controller` for the select and checkbox controls.
   *
   * `register` only works on controls the browser knows about. A Radix
   * dropdown is a button and a portalled listbox, so its value has to be
   * bridged into the form explicitly. Still the three pieces the field set
   * needs rather than the whole form object, so it cannot reach `submit`.
   */
  readonly control: Control<TenantFormInput>;
}

/**
 * A tenant's inputs.
 *
 * Country, state and city are cascading selects: clearing a parent resets the
 * children. The form stores the empty string for "no selection" so the
 * <select> always has an explicit value.
 */
/**
 * What each module is called, and what buying it gets you.
 *
 * The API answers with codes because the catalogue is a constant tied to
 * shipped features, not a table of copy. Rendering `PMS` and `PROPERTY` at a
 * person is asking them to read an enum: the code is what the system calls
 * it, and this is what a customer is being sold.
 *
 * Unknown codes fall back to the code itself, so a module shipped by a newer
 * server still appears and can still be sold -- unnamed is better than
 * missing.
 */
const MODULE_COPY: Readonly<Record<string, { readonly label: string; readonly blurb: string }>> = {
  CORE: { label: 'Core', blurb: 'Users, roles and the audit log. Always included.' },
  PROPERTY: { label: 'Properties & rooms', blurb: 'Property records, room types and inventory.' },
  BOOKING: { label: 'Bookings', blurb: 'Reservations, guests and availability.' },
  PRICING: { label: 'Pricing', blurb: 'Rate plans, rates and promotions.' },
  PROMOTIONS: { label: 'Promotions', blurb: 'Campaigns and discounts applied at booking.' },
  PAYMENTS: { label: 'Payments', blurb: 'Gateways, captures and refunds.' },
  RULES: { label: 'Business rules', blurb: 'Conditions that drive pricing and availability.' },
  NOTIFICATIONS: { label: 'Notifications', blurb: 'Guest and staff email and SMS.' },
  PMS: { label: 'PMS & integrations', blurb: 'Property-management sync and third-party links.' },
};
const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedSearchTerm(value: string, delay = SEARCH_DEBOUNCE_MS): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

export function TenantFields({ register, errors, control }: TenantFieldsProps): React.JSX.Element {
  // The field name is a second type argument, not just a string. Without it
  // `useWatch` widens to the union of every field's type — `string | boolean |
  // undefined` here — and the cascade below stops type-checking against the
  // query hooks, which take a plain id.
  // `control` passed explicitly, not read from context: the form pages render
  // these fields without a `FormProvider`, and a context-less `useWatch`
  // throws on the first render rather than degrading.
  const countryId = useWatch<TenantFormInput, 'countryId'>({ control, name: 'countryId' });
  const stateId = useWatch<TenantFormInput, 'stateId'>({ control, name: 'stateId' });

  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  const debouncedCountrySearch = useDebouncedSearchTerm(countrySearch);
  const debouncedStateSearch = useDebouncedSearchTerm(stateSearch);
  const debouncedCitySearch = useDebouncedSearchTerm(citySearch);

  const catalogueQuery = useModuleCatalogue();
  const countriesQuery = useCountries(debouncedCountrySearch);
  const statesQuery = useStates(countryId, debouncedStateSearch, countriesQuery.data);
  const citiesQuery = useCities(
    countryId,
    stateId,
    debouncedCitySearch,
    countriesQuery.data,
    statesQuery.data,
  );

  // No empty-valued entry: "nothing chosen" is the select's placeholder, and
  // Radix throws on an item whose value is the empty string.
  const countryOptions = (countriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const stateOptions = (statesQuery.data ?? []).map((state) => ({
    value: state.id,
    label: state.name,
  }));

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'SUSPENDED', label: 'Suspended' },
  ];

  const cityOptions = (citiesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.name?.message} required>
          <FieldLabel>Tenant name</FieldLabel>
          <FieldInput autoComplete="off" {...register('name')} />
        </Field>

        <Field error={errors.slug?.message} required>
          <FieldLabel>Slug</FieldLabel>
          <FieldInput autoComplete="off" placeholder="my-tenant" {...register('slug')} />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.status?.message} required>
          <FieldLabel>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={statusOptions}
                placeholder={'Select a status'}
                searchable
              />
            )}
          />
        </Field>

        <Field
          error={errors.timezone?.message}
          required
          description="The clock this tenant's staff read every date and time against."
        >
          <FieldLabel>Timezone</FieldLabel>
          <Controller
            control={control}
            name="timezone"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={TIMEZONE_OPTIONS}
                placeholder="Select a timezone"
                searchable
              />
            )}
          />
        </Field>
      </div>

      <Field error={errors.contactName?.message}>
        <FieldLabel>Contact name</FieldLabel>
        <FieldInput autoComplete="off" {...register('contactName')} />
      </Field>

      <Field error={errors.email?.message}>
        <FieldLabel>Email</FieldLabel>
        <FieldInput autoComplete="off" type="email" {...register('email')} />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field error={errors.phoneExtension?.message}>
          <FieldLabel>Dial Extension</FieldLabel>
          <Controller
            control={control}
            name="phoneExtension"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onSearchChange={setCountrySearch}
                onBlur={field.onBlur}
                options={dialCodeOptions(countriesQuery.data ?? [], field.value)}
                placeholder="Select a dial code"
                searchable
              />
            )}
          />
        </Field>
        <Field error={errors.phone?.message}>
          <FieldLabel>Phone</FieldLabel>
          <FieldInput autoComplete="off" {...register('phone')} />
        </Field>

        <Field error={errors.address?.message}>
          <FieldLabel>Address</FieldLabel>
          <FieldInput autoComplete="off" {...register('address')} />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field error={errors.countryId?.message}>
          <FieldLabel>Country</FieldLabel>
          <Controller
            control={control}
            name="countryId"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setStateSearch('');
                  setCitySearch('');
                }}
                onSearchChange={setCountrySearch}
                onBlur={field.onBlur}
                options={countryOptions}
                placeholder="Select a country"
              />
            )}
          />
        </Field>

        <Field error={errors.stateId?.message}>
          <FieldLabel>State / Province</FieldLabel>
          <Controller
            control={control}
            name="stateId"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setCitySearch('');
                }}
                onSearchChange={setStateSearch}
                onBlur={field.onBlur}
                options={stateOptions}
                disabled={countryId === ''}
                placeholder={countryId === '' ? 'Select a country first' : 'Select a state'}
              />
            )}
          />
        </Field>

        <Field error={errors.cityId?.message}>
          <FieldLabel>City</FieldLabel>
          <Controller
            control={control}
            name="cityId"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onSearchChange={setCitySearch}
                onBlur={field.onBlur}
                options={cityOptions}
                disabled={stateId === ''}
                placeholder={stateId === '' ? 'Select a state first' : 'Select a city'}
                searchable
              />
            )}
          />
        </Field>
      </div>

      <Field error={errors.enabledModules?.message}>
        {/* A fieldset and legend rather than a FieldLabel: one Field mints one
            control id, so a label pointing at it would name whichever checkbox
            rendered first and leave the rest unlabelled. The legend names the
            group for every box in it, which is what a checkbox group needs. */}
        <fieldset>
          <legend className="text-sm font-medium leading-none">Modules</legend>
          <p className="text-muted-foreground mt-1 mb-3 text-sm">
            What this tenant has bought. Staff only reach features their tenant&apos;s modules
            include, so unticking one hides it for everyone here.
          </p>
          <Controller
            control={control}
            name="enabledModules"
            render={({ field }) => (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(catalogueQuery.data ?? []).map((module) => {
                  const copy = MODULE_COPY[module.code];
                  const checked = !module.assignable || field.value.includes(module.code);
                  return (
                    // A bordered tile that reacts to its own state, rather than a
                    // bare checkbox in a grid: the whole row is the target, and
                    // ticked and unticked are distinguishable at a glance.
                    <Field
                      key={module.code}
                      className={cn(
                        'rounded-lg border p-3 transition-colors',
                        checked ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/40',
                        !module.assignable && 'opacity-70',
                      )}
                    >
                      <FieldCheckbox
                        // CORE is shown checked and disabled rather than hidden,
                        // so the rule is visible. Hiding it makes an operator
                        // wonder why user management works on a tenant with no
                        // modules.
                        checked={checked}
                        disabled={!module.assignable}
                        onCheckedChange={(next) => {
                          field.onChange(
                            next
                              ? [...field.value, module.code]
                              : field.value.filter((code) => code !== module.code),
                          );
                        }}
                        onBlur={field.onBlur}
                      >
                        <span className="font-medium">{copy?.label ?? module.code}</span>
                      </FieldCheckbox>
                      <p className="text-muted-foreground mt-1 pl-6 text-xs">
                        {copy?.blurb ?? `${String(module.permissions.length)} permissions`}
                        {module.assignable ? null : ' Cannot be removed.'}
                      </p>
                    </Field>
                  );
                })}
              </div>
            )}
          />
        </fieldset>
      </Field>
    </div>
  );
}
